import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Chart } from "primereact/chart";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Divider } from "primereact/divider";
import { useLoading } from "../components/Loading/LoadingProvider";
import axiosInstance from "../utils/axiosInstance";
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, HeadingLevel, BorderStyle } from 'docx';
import { useAuth } from "../utils/AuthProvider";

const Dashboard = () => {
    const { user } = useAuth();
    const toast = useRef(null);
    const { setLoading } = useLoading();
    const [paymentData, setPaymentData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [overdueVisible, setOverdueVisible] = useState(false);
    const [dueTodayVisible, setDueTodayVisible] = useState(false);
    const [selectedTimeRange, setSelectedTimeRange] = useState('all');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [chartData, setChartData] = useState(null);
    const [yearlyChartData, setYearlyChartData] = useState({});
    const [selectedChartYear, setSelectedChartYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [summaryData, setSummaryData] = useState({
        totalPayments: 0,
        paidPayments: 0,
        pendingPayments: 0,
        overduePayments: 0,
        dueTodayPayments: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
        overdueAmount: 0,
        dueTodayAmount: 0,
        totalPrincipal: 0,
        totalInterest: 0
    });
    const [overduePayments, setOverduePayments] = useState([]);
    const [dueTodayPayments, setDueTodayPayments] = useState([]);
    const [selectedOverduePayment, setSelectedOverduePayment] = useState(null);
    const [overdueDialogVisible, setOverdueDialogVisible] = useState(false);
    const alertRef = useRef(null);

    // Diğer state'ler
    const [summaryVisible, setSummaryVisible] = useState(true);
    const [tablesVisible, setTablesVisible] = useState(true);
    const [chartsVisible, setChartsVisible] = useState(true);
    const [activeFilterPanel, setActiveFilterPanel] = useState(false);

    const timeRangeOptions = [
        { label: 'Bugün', value: 'today' },
        { label: 'Bu Hafta', value: 'thisWeek' },
        { label: 'Bu Ay', value: 'thisMonth' },
        { label: 'Haftalık', value: 'week' },
        { label: 'Aylık', value: 'month' },
        { label: 'Yıllık', value: 'year' },
        { label: 'Tümü', value: 'all' }
    ];


    const chartColors = {
        paid: {
            light: 'rgba(54, 162, 235, 0.8)',
            dark: 'rgba(54, 162, 235, 0.8)'
        },
        pending: {
            light: 'rgba(255, 206, 86, 0.8)',
            dark: 'rgba(255, 206, 86, 0.8)'
        },
        overdue: {
            light: 'rgba(255, 99, 132, 0.8)',
            dark: 'rgba(255, 99, 132, 0.8)'
        },
        dueToday: {
            light: 'rgba(255, 165, 0, 0.8)',
            dark: 'rgba(255, 165, 0, 0.8)'
        },
        principal: {
            light: 'rgba(75, 192, 192, 0.8)',
            dark: 'rgba(75, 192, 192, 0.8)'
        },
        interest: {
            light: 'rgba(153, 102, 255, 0.8)',
            dark: 'rgba(153, 102, 255, 0.8)'
        }
    };

    // Sayısal değerleri güvenli bir şekilde parse etme fonksiyonu
    const parseNumber = (value) => {
        if (value === null || value === undefined) return 0;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? 0 : parsed;
        }
        return 0;
    };

    useEffect(() => {
        fetchPaymentData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Veri yüklendiğinde filtreleme ve müşteri seçenekleri oluşturma
    useEffect(() => {
        if (paymentData && paymentData.length > 0) {
            generateCustomerOptions();
            generateAvailableYears();
            applyFilters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentData]);

    // Filtreler değiştiğinde yeniden filtreleme
    useEffect(() => {
        if (paymentData && paymentData.length > 0) {
            applyFilters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTimeRange, selectedCustomer, dateRange]);

    // Filtrelenmiş veri değiştiğinde özet ve grafik verilerini güncelleme
    useEffect(() => {
        if (filteredData && filteredData.length > 0) {
            generateChartDataByYear();
            calculateSummaryData();
            checkOverduePayments();
            checkDueTodayPayments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredData]);

    // Gecikmiş ödemeler değiştiğinde bildirim gösterme/gizleme
    useEffect(() => {
        if (overduePayments.length > 0) {
            setOverdueVisible(true);
            const interval = setInterval(() => {
                if (alertRef.current) {
                    alertRef.current.style.opacity = alertRef.current.style.opacity === '1' ? '0.5' : '1';
                }
            }, 500);

            return () => clearInterval(interval);
        } else {
            setOverdueVisible(false);
        }
    }, [overduePayments]);

    // Bugün ödemeleri değiştiğinde bildirim gösterme/gizleme
    useEffect(() => {
        if (dueTodayPayments.length > 0) {
            setDueTodayVisible(true);
        } else {
            setDueTodayVisible(false);
        }
    }, [dueTodayPayments]);

    // Seçili yıl değiştiğinde chart verilerini güncelleme
    useEffect(() => {
        if (Object.keys(yearlyChartData).length > 0 && selectedChartYear) {
            updateChartDataForSelectedYear();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChartYear, yearlyChartData]);

    // Zaman aralığı seçildiğinde otomatik tarih aralığı doldurma
    useEffect(() => {
        if (selectedTimeRange && selectedTimeRange !== 'all') {
            setDateRangeFromTimeRange(selectedTimeRange);
        }
    }, [selectedTimeRange]);

    const setDateRangeFromTimeRange = (timeRange) => {
        const today = new Date();
        let startDate = new Date();
        let endDate = new Date();

        switch (timeRange) {
            case 'today':
                // Bugün için başlangıç ve bitiş tarihini bugün olarak ayarla
                break;
            case 'thisWeek':
                // Bu haftanın başlangıcını bulma (Pazartesi)
                const dayOfWeek = today.getDay() || 7; // getDay 0-6 döner, Pazar 0, Pazartesi 1
                startDate.setDate(today.getDate() - dayOfWeek + 1);
                endDate.setDate(startDate.getDate() + 6);
                break;
            case 'thisMonth':
                // Bu ayın başlangıcını bulma
                startDate.setDate(1);
                endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            case 'week':
                // Son 7 gün
                startDate.setDate(today.getDate() - 7);
                break;
            case 'month':
                // Son 30 gün
                startDate.setDate(today.getDate() - 30);
                break;
            case 'year':
                // Son 365 gün
                startDate.setDate(today.getDate() - 365);
                break;
            default:
                setDateRange([null, null]);
                return;
        }

        // Tarihleri sıfırla (saat, dakika, saniye)
        startDate?.setHours(0, 0, 0, 0);
        endDate?.setHours(23, 59, 59, 999);

        setDateRange([startDate, endDate]);
    };

    const fetchPaymentData = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/v1/payment/payment-details/by-seller', {
                params: {
                    seller: user.username
                }
            });

            if (response?.status === 200) {
                if (response.data && Array.isArray(response.data)) {

                    // Veriyi kontrol et ve düzelt
                    const processedData = response.data.map(item => ({
                        ...item,
                        installmentPrincipalAmount: parseNumber(item.installmentPrincipalAmount),
                        installmentInterestAmount: parseNumber(item.installmentInterestAmount),
                        totalAmountOfInstallments: parseNumber(item.totalAmountOfInstallments)
                    }));

                    setPaymentData(processedData);
                } else {
                    console.error("API yanıtı dizi değil:", response.data);
                    setPaymentData([]);
                    toast.current.show({
                        severity: "warn",
                        summary: "Uyarı",
                        detail: "Veri formatı geçersiz",
                        life: 3000,
                    });
                }
            }
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Hata",
                detail: `Ödeme takip verileri getirilirken bir hata oluştu: ${error.message || error}`,
                life: 3000,
            });
            console.error('Veriler yüklenirken hata oluştu:', error);
            setPaymentData([]);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1 * 1000);
        }
    };

    const generateAvailableYears = () => {
        try {
            const years = [...new Set(paymentData.map(payment => {
                if (!payment.installmentDate) return null;
                return new Date(payment.installmentDate).getFullYear();
            }).filter(Boolean))];

            // Yılları sırala
            years.sort();

            setAvailableYears(years);

            // Varsayılan olarak cari yıl seçili olsun, yoksa ilk yıl
            const currentYear = new Date().getFullYear();
            if (years.includes(currentYear)) {
                setSelectedChartYear(currentYear);
            } else if (years.length > 0) {
                setSelectedChartYear(years[0]);
            }
        } catch (error) {
            console.error('Yıl listesi oluşturma hatası:', error);
            setAvailableYears([]);
        }
    };

    const generateCustomerOptions = () => {
        try {
            const uniqueCustomers = [...new Set(paymentData.map(payment => {
                if (!payment.customerName || !payment.customerSurname) return null;
                return `${payment.customerName} ${payment.customerSurname}`;
            }).filter(Boolean))];

            const options = uniqueCustomers.map(customer => ({
                label: customer,
                value: customer
            }));

            setCustomerOptions(options);
        } catch (error) {
            console.error('Müşteri listesi oluşturma hatası:', error);
            setCustomerOptions([]);
        }
    };

    const resetFilters = () => {
        setSelectedTimeRange('all');
        setSelectedCustomer(null);
        setDateRange([null, null]);
        toast.current.show({
            severity: "info",
            summary: "Filtreler Sıfırlandı",
            detail: "Tüm filtreler varsayılan değerlere döndürüldü.",
            life: 2000,
        });
    };

    const applyFilters = () => {
        try {
            let filtered = [...paymentData];

            // Müşteri filtresini uygula
            if (selectedCustomer) {
                filtered = filtered.filter(payment => {
                    if (!payment || !payment.customerName || !payment.customerSurname) return false;
                    return `${payment.customerName} ${payment.customerSurname}` === selectedCustomer;
                });
            }

            // Tarih aralığı filtresini uygula
            if (dateRange[0] && dateRange[1]) {
                const startDate = new Date(dateRange[0]);
                startDate.setHours(0, 0, 0, 0);

                const endDate = new Date(dateRange[1]);
                endDate.setHours(23, 59, 59, 999);

                filtered = filtered.filter(payment => {
                    if (!payment || !payment.installmentDate) return false;
                    const installmentDate = new Date(payment.installmentDate);
                    return installmentDate >= startDate && installmentDate <= endDate;
                });
            }
            setFilteredData(filtered);
        } catch (error) {
            console.error('Filtreleme hatası:', error);
            setFilteredData([...paymentData]);
        }
    };

    const checkOverduePayments = () => {
        try {
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0); // Bugünün başlangıcını al

            const overdue = filteredData.filter(payment => {
                if (!payment || payment.isPaid === undefined || !payment.installmentDate) return false;

                // isPaid kontrolü
                const isPaid = payment.isPaid && payment.isPaid.id === 1;
                if (isPaid) return false;

                const installmentDate = new Date(payment.installmentDate);
                installmentDate.setHours(0, 0, 0, 0);

                // Bugünden önceki ödenmemiş ödemeler
                return installmentDate < currentDate;
            });
            setOverduePayments(overdue);
        } catch (error) {
            console.error('Gecikmiş ödeme hesaplama hatası:', error);
            setOverduePayments([]);
        }
    };

    const checkDueTodayPayments = () => {
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Günün başlangıcı

            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1); // Yarının başlangıcı

            const dueTodayItems = filteredData.filter(payment => {
                if (!payment || payment.isPaid === undefined || !payment.installmentDate) return false;

                // isPaid kontrolü
                const isPaid = payment.isPaid && payment.isPaid.id === 1;
                if (isPaid) return false;

                const installmentDate = new Date(payment.installmentDate);
                installmentDate.setHours(0, 0, 0, 0);

                // Bugün ödenecek ödemeler
                return installmentDate.getTime() === today.getTime();
            });
            setDueTodayPayments(dueTodayItems);
        } catch (error) {
            console.error('Bugün ödenecek ödeme hesaplama hatası:', error);
            setDueTodayPayments([]);
        }
    };

    const calculateSummaryData = () => {
        try {
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0); // Günün başlangıcı

            const total = filteredData.length;

            const paid = filteredData.filter(payment => {
                return payment && payment.isPaid && payment.isPaid.id === 1;
            }).length;

            const pending = filteredData.filter(payment => {
                if (!payment || !payment.isPaid || !payment.installmentDate) return false;
                const isPaid = payment.isPaid.id === 1;
                if (isPaid) return false;

                const installmentDate = new Date(payment.installmentDate);
                installmentDate.setHours(0, 0, 0, 0);
                return installmentDate > currentDate; // Bugünden sonraki ödemeler
            }).length;

            const overdue = filteredData.filter(payment => {
                if (!payment || !payment.isPaid || !payment.installmentDate) return false;
                const isPaid = payment.isPaid.id === 1;
                if (isPaid) return false;

                const installmentDate = new Date(payment.installmentDate);
                installmentDate.setHours(0, 0, 0, 0);
                return installmentDate < currentDate; // Bugünden önceki ödemeler
            }).length;

            const dueToday = filteredData.filter(payment => {
                if (!payment || !payment.isPaid || !payment.installmentDate) return false;
                const isPaid = payment.isPaid.id === 1;
                if (isPaid) return false;

                const installmentDate = new Date(payment.installmentDate);
                installmentDate.setHours(0, 0, 0, 0);
                return installmentDate.getTime() === currentDate.getTime(); // Bugünkü ödemeler
            }).length;

            // Tüm sayısal değerleri güvenli parse ediyoruz
            const totalAmount = filteredData.reduce((sum, payment) => {
                return sum + parseNumber(payment.totalAmountOfInstallments);
            }, 0);

            const paidAmount = filteredData
                .filter(payment => payment && payment.isPaid && payment.isPaid.id === 1)
                .reduce((sum, payment) => sum + parseNumber(payment.totalAmountOfInstallments), 0);

            const pendingAmount = filteredData
                .filter(payment => {
                    if (!payment || !payment.isPaid || !payment.installmentDate) return false;
                    const isPaid = payment.isPaid.id === 1;
                    if (isPaid) return false;

                    const installmentDate = new Date(payment.installmentDate);
                    installmentDate.setHours(0, 0, 0, 0);
                    return installmentDate > currentDate;
                })
                .reduce((sum, payment) => sum + parseNumber(payment.totalAmountOfInstallments), 0);

            const overdueAmount = filteredData
                .filter(payment => {
                    if (!payment || !payment.isPaid || !payment.installmentDate) return false;
                    const isPaid = payment.isPaid.id === 1;
                    if (isPaid) return false;

                    const installmentDate = new Date(payment.installmentDate);
                    installmentDate.setHours(0, 0, 0, 0);
                    return installmentDate < currentDate;
                })
                .reduce((sum, payment) => sum + parseNumber(payment.totalAmountOfInstallments), 0);

            const dueTodayAmount = filteredData
                .filter(payment => {
                    if (!payment || !payment.isPaid || !payment.installmentDate) return false;
                    const isPaid = payment.isPaid.id === 1;
                    if (isPaid) return false;

                    const installmentDate = new Date(payment.installmentDate);
                    installmentDate.setHours(0, 0, 0, 0);
                    return installmentDate.getTime() === currentDate.getTime();
                })
                .reduce((sum, payment) => sum + parseNumber(payment.totalAmountOfInstallments), 0);

            // Anapara ve Faiz toplamları
            const totalPrincipal = filteredData.reduce((sum, payment) => {
                return sum + parseNumber(payment.installmentPrincipalAmount);
            }, 0);

            const totalInterest = filteredData.reduce((sum, payment) => {
                return sum + parseNumber(payment.installmentInterestAmount);
            }, 0);

            const summary = {
                totalPayments: total,
                paidPayments: paid,
                pendingPayments: pending,
                overduePayments: overdue,
                dueTodayPayments: dueToday,
                totalAmount,
                paidAmount,
                pendingAmount,
                overdueAmount,
                dueTodayAmount,
                totalPrincipal,
                totalInterest
            };
            setSummaryData(summary);
        } catch (error) {
            console.error('Özet hesaplama hatası:', error);
        }
    };

    const generateChartDataByYear = () => {
        try {
            const yearData = {};

            // Önce yıl bazında veri yapısını hazırla
            filteredData.forEach(payment => {
                if (!payment || !payment.installmentDate) return;

                const installmentDate = new Date(payment.installmentDate);
                const year = installmentDate.getFullYear();
                const month = installmentDate.getMonth();

                // Her yıl için bir yapı oluştur
                if (!yearData[year]) {
                    yearData[year] = Array(12).fill().map(() => ({
                        paid: 0,
                        pending: 0,
                        overdue: 0,
                        dueToday: 0,
                        principalAmount: 0,
                        interestAmount: 0
                    }));
                }

                // Güvenli sayı dönüşümü
                const principalAmount = parseNumber(payment.installmentPrincipalAmount);
                const interestAmount = parseNumber(payment.installmentInterestAmount);
                const totalAmount = parseNumber(payment.totalAmountOfInstallments);

                yearData[year][month].principalAmount += principalAmount;
                yearData[year][month].interestAmount += interestAmount;

                const isPaid = payment.isPaid && payment.isPaid.id === 1;

                // Bugün
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Ödeme tarihi
                const paymentDate = new Date(payment.installmentDate);
                paymentDate.setHours(0, 0, 0, 0);

                // Ödeme durumuna göre gruplama
                if (isPaid) {
                    yearData[year][month].paid += totalAmount;
                } else if (paymentDate.getTime() === today.getTime()) {
                    yearData[year][month].dueToday += totalAmount;
                } else if (paymentDate < today) {
                    yearData[year][month].overdue += totalAmount;
                } else {
                    yearData[year][month].pending += totalAmount;
                }
            });
            setYearlyChartData(yearData);

            // Eğer seçili yıl için veri yoksa, varsayılan yılı ayarla
            if (selectedChartYear && !yearData[selectedChartYear] && Object.keys(yearData).length > 0) {
                setSelectedChartYear(parseInt(Object.keys(yearData)[0]));
            }

            // İlk yükleme için mevcut yılın grafiğini oluştur
            updateChartDataForSelectedYear();
        } catch (error) {
            console.error('Yıllık grafik verisi oluşturma hatası:', error);
        }
    };

    const updateChartDataForSelectedYear = () => {
        try {
            if (!yearlyChartData || !yearlyChartData[selectedChartYear]) {
                setChartData(null);
                return;
            }

            const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

            const selectedYearData = yearlyChartData[selectedChartYear];

            setChartData({
                labels: months,
                datasets: [
                    {
                        label: 'Ödenen',
                        backgroundColor: chartColors.paid.light,
                        data: months.map((_, index) => selectedYearData[index].paid)
                    },
                    {
                        label: 'Bekleyen',
                        backgroundColor: chartColors.pending.light,
                        data: months.map((_, index) => selectedYearData[index].pending)
                    },
                    {
                        label: 'Bugün Ödenecek',
                        backgroundColor: chartColors.dueToday.light,
                        data: months.map((_, index) => selectedYearData[index].dueToday)
                    },
                    {
                        label: 'Gecikmiş',
                        backgroundColor: chartColors.overdue.light,
                        data: months.map((_, index) => selectedYearData[index].overdue)
                    },
                    {
                        label: 'Anapara',
                        backgroundColor: chartColors.principal.light,
                        borderColor: chartColors.principal.light,
                        fill: false,
                        data: months.map((_, index) => selectedYearData[index].principalAmount),
                        type: 'line'
                    },
                    {
                        label: 'Faiz',
                        backgroundColor: chartColors.interest.light,
                        borderColor: chartColors.interest.light,
                        fill: false,
                        data: months.map((_, index) => selectedYearData[index].interestAmount),
                        type: 'line'
                    }
                ]
            });
        } catch (error) {
            console.error('Seçili yıl grafik verisi oluşturma hatası:', error);
            setChartData(null);
        }
    };

    const formatCurrency = (value) => {
        try {
            // Değeri önce güvenli bir şekilde sayıya dönüştür
            const numValue = parseNumber(value);
            return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(numValue);
        } catch (error) {
            console.error('Para formatı hatası:', error);
            return '₺0,00';
        }
    };

    const formatDate = (value) => {
        try {
            // Tarih yoksa veya geçersizse - döndür
            if (!value) return ' ';
            return new Date(value).toLocaleDateString('tr-TR');
        } catch (error) {
            console.error('Tarih formatı hatası:', error);
            return ' ';
        }
    };

    const paymentStatusTemplate = (rowData) => {
        try {
            if (!rowData || !rowData.isPaid) return <Tag severity="info" value="Bilinmiyor" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs sm:text-sm" />;

            const isPaid = rowData.isPaid.id === 1;

            if (isPaid) {
                return <Tag severity="success" value="Ödendi" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 text-xs sm:text-sm" />;
            }

            const installmentDate = new Date(rowData.installmentDate);
            installmentDate.setHours(0, 0, 0, 0);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (installmentDate.getTime() === today.getTime()) {
                return <Tag severity="warning" value="Bugün" className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 text-xs sm:text-sm" />;
            } else if (installmentDate < today) {
                return <Tag severity="danger" value="Gecikmiş" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 text-xs sm:text-sm" />;
            } else {
                return <Tag severity="info" value="Vadesi Gelmemiş" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs sm:text-sm" />;
            }
        } catch (error) {
            console.error('Ödeme durumu şablonu hatası:', error);
            return <Tag severity="info" value="Bilinmiyor" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs sm:text-sm" />;
        }
    };

    const showOverdueDetail = (payment) => {
        setSelectedOverduePayment(payment);
        setOverdueDialogVisible(true);
    };

    const getUpcomingPayments = () => {
        try {
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            const nextMonth = new Date(currentDate);
            nextMonth.setMonth(nextMonth.getMonth() + 1);

            return filteredData.filter(payment => {
                if (!payment || !payment.installmentDate || !payment.isPaid) return false;

                const isPaid = payment.isPaid.id === 1;
                if (isPaid) return false;

                const installmentDate = new Date(payment.installmentDate);
                installmentDate.setHours(0, 0, 0, 0);
                return installmentDate >= currentDate && installmentDate <= nextMonth;
            }).sort((a, b) => new Date(a.installmentDate) - new Date(b.installmentDate));
        } catch (error) {
            console.error('Yaklaşan ödemeler hesaplama hatası:', error);
            return [];
        }
    };

    const getRecentPayments = () => {
        try {
            const currentDate = new Date();
            const lastMonth = new Date();
            lastMonth.setMonth(currentDate.getMonth() - 1);

            return filteredData.filter(payment => {
                if (!payment || !payment.isPaid) return false;

                const isPaid = payment.isPaid.id === 1;
                if (!isPaid) return false;

                const paidDate = payment.paidDate ? new Date(payment.paidDate) : null;
                return paidDate && paidDate >= lastMonth && paidDate <= currentDate;
            }).sort((a, b) => {
                if (!a.paidDate || !b.paidDate) return 0;
                return new Date(b.paidDate) - new Date(a.paidDate);
            });
        } catch (error) {
            console.error('Son ödemeler hesaplama hatası:', error);
            return [];
        }
    };

    const getTodayPayments = () => {
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            return filteredData.filter(payment => {
                if (!payment || !payment.installmentDate || !payment.isPaid) return false;

                const isPaid = payment.isPaid.id === 1;
                if (isPaid) return false;

                const installmentDate = new Date(payment.installmentDate);
                installmentDate.setHours(0, 0, 0, 0);

                return installmentDate.getTime() === today.getTime();
            }).sort((a, b) => parseNumber(a.installmentOrder) - parseNumber(b.installmentOrder));
        } catch (error) {
            console.error('Bugünkü ödemeler hesaplama hatası:', error);
            return [];
        }
    };

    // Word'e veri aktarma fonksiyonu
    const exportToWord = () => {
        setLoading(true);
        try {
            // Word dökümanı oluştur
            const doc = new Document({
                title: "Ödeme Takip Raporu",
                description: "Ödeme takip sistemi raporu",
                styles: {
                    paragraphStyles: [
                        {
                            id: "Heading1",
                            name: "Heading 1",
                            run: {
                                size: 36,
                                bold: true,
                                color: "000000"
                            },
                            paragraph: {
                                spacing: {
                                    after: 240
                                }
                            }
                        },
                        {
                            id: "Heading2",
                            name: "Heading 2",
                            run: {
                                size: 28,
                                bold: true,
                                color: "000000"
                            },
                            paragraph: {
                                spacing: {
                                    after: 120
                                }
                            }
                        }
                    ]
                }
            });

            // Başlık ekle
            doc.addSection({
                children: [
                    new Paragraph({
                        text: "Ödeme Takip Raporu",
                        heading: HeadingLevel.HEADING_1,
                        alignment: "center"
                    }),
                    new Paragraph({
                        text: `Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')}`,
                        alignment: "center"
                    }),
                    new Paragraph({
                        text: `Satıcı: seyfullahcaliskan`,
                        alignment: "center"
                    }),
                    new Paragraph({
                        text: " "
                    }),

                    // Özet bilgileri
                    new Paragraph({
                        text: "Özet Bilgiler",
                        heading: HeadingLevel.HEADING_2
                    }),
                    new Table({
                        width: {
                            size: 100,
                            type: "pct"
                        },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "auto" }
                        },
                        rows: [
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Metrik")] }),
                                    new TableCell({ children: [new Paragraph("Sayı")] }),
                                    new TableCell({ children: [new Paragraph("Tutar")] })
                                ],
                                tableHeader: true
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Toplam Ödemeler")] }),
                                    new TableCell({ children: [new Paragraph(`${summaryData.totalPayments}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(summaryData.totalAmount)}`)] })
                                ]
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Ödenen")] }),
                                    new TableCell({ children: [new Paragraph(`${summaryData.paidPayments}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(summaryData.paidAmount)}`)] })
                                ]
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Bekleyen")] }),
                                    new TableCell({ children: [new Paragraph(`${summaryData.pendingPayments}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(summaryData.pendingAmount)}`)] })
                                ]
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Bugün Ödenecek")] }),
                                    new TableCell({ children: [new Paragraph(`${summaryData.dueTodayPayments}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(summaryData.dueTodayAmount)}`)] })
                                ]
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Gecikmiş")] }),
                                    new TableCell({ children: [new Paragraph(`${summaryData.overduePayments}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(summaryData.overdueAmount)}`)] })
                                ]
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Anapara")] }),
                                    new TableCell({ children: [new Paragraph("-")] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(summaryData.totalPrincipal)}`)] })
                                ]
                            }),
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Faiz")] }),
                                    new TableCell({ children: [new Paragraph("-")] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(summaryData.totalInterest)}`)] })
                                ]
                            })
                        ]
                    }),
                    new Paragraph({ text: " " }),

                    // Bugünkü Ödemeler
                    new Paragraph({
                        text: "Bugün Ödenecek Ödemeler",
                        heading: HeadingLevel.HEADING_2
                    }),
                    new Table({
                        width: {
                            size: 100,
                            type: "pct"
                        },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "auto" }
                        },
                        rows: [
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Taksit No")] }),
                                    new TableCell({ children: [new Paragraph("Müşteri")] }),
                                    new TableCell({ children: [new Paragraph("Anapara")] }),
                                    new TableCell({ children: [new Paragraph("Faiz")] }),
                                    new TableCell({ children: [new Paragraph("Toplam")] })
                                ],
                                tableHeader: true
                            }),
                            ...getTodayPayments().map(payment => new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph(`${payment.installmentOrder || 0}`)] }),
                                    new TableCell({ children: [new Paragraph(`${payment.customerName || ''} ${payment.customerSurname || ''}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(payment.installmentPrincipalAmount)}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(payment.installmentInterestAmount)}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(payment.totalAmountOfInstallments)}`)] })
                                ]
                            }))
                        ]
                    }),
                    new Paragraph({ text: " " }),

                    // Yaklaşan Ödemeler
                    new Paragraph({
                        text: "Yaklaşan Ödemeler (30 gün içinde)",
                        heading: HeadingLevel.HEADING_2
                    }),
                    new Table({
                        width: {
                            size: 100,
                            type: "pct"
                        },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "auto" }
                        },
                        rows: [
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Taksit No")] }),
                                    new TableCell({ children: [new Paragraph("Müşteri")] }),
                                    new TableCell({ children: [new Paragraph("Ödeme Tarihi")] }),
                                    new TableCell({ children: [new Paragraph("Anapara")] }),
                                    new TableCell({ children: [new Paragraph("Faiz")] }),
                                    new TableCell({ children: [new Paragraph("Toplam")] })
                                ],
                                tableHeader: true
                            }),
                            ...getUpcomingPayments().map(payment => new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph(`${payment.installmentOrder || 0}`)] }),
                                    new TableCell({ children: [new Paragraph(`${payment.customerName || ''} ${payment.customerSurname || ''}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatDate(payment.installmentDate)}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(payment.installmentPrincipalAmount)}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(payment.installmentInterestAmount)}`)] }),
                                    new TableCell({ children: [new Paragraph(`${formatCurrency(payment.totalAmountOfInstallments)}`)] })
                                ]
                            }))
                        ]
                    }),
                    new Paragraph({ text: " " }),

                    // Gecikmiş Ödemeler
                    new Paragraph({
                        text: "Gecikmiş Ödemeler",
                        heading: HeadingLevel.HEADING_2
                    }),
                    new Table({
                        width: {
                            size: 100,
                            type: "pct"
                        },
                        borders: {
                            top: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            bottom: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            left: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            right: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "auto" },
                            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "auto" }
                        },
                        rows: [
                            new TableRow({
                                children: [
                                    new TableCell({ children: [new Paragraph("Taksit No")] }),
                                    new TableCell({ children: [new Paragraph("Müşteri")] }),
                                    new TableCell({ children: [new Paragraph("Ödeme Tarihi")] }),
                                    new TableCell({ children: [new Paragraph("Gecikme (gün)")] }),
                                    new TableCell({ children: [new Paragraph("Toplam Tutar")] })
                                ],
                                tableHeader: true
                            }),
                            ...overduePayments.map(payment => {
                                const daysLate = payment.installmentDate ?
                                    Math.floor((new Date() - new Date(payment.installmentDate)) / (1000 * 60 * 60 * 24)) : 0;
                                return new TableRow({
                                    children: [
                                        new TableCell({ children: [new Paragraph(`${payment.installmentOrder || 0}`)] }),
                                        new TableCell({ children: [new Paragraph(`${payment.customerName || ''} ${payment.customerSurname || ''}`)] }),
                                        new TableCell({ children: [new Paragraph(`${formatDate(payment.installmentDate)}`)] }),
                                        new TableCell({ children: [new Paragraph(`${daysLate}`)] }),
                                        new TableCell({ children: [new Paragraph(`${formatCurrency(payment.totalAmountOfInstallments)}`)] })
                                    ]
                                });
                            })
                        ]
                    })
                ]
            });

            // Word dosyasını oluştur ve indir
            Packer.toBlob(doc).then(blob => {
                saveAs(blob, "Odeme_Takip_Raporu.docx");
                toast.current.show({
                    severity: "success",
                    summary: "Başarılı",
                    detail: "Rapor Word dosyası olarak indirildi.",
                    life: 3000,
                });
                setLoading(false);
            });
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: `Word dosyası oluşturulurken bir hata oluştu: ${error.message || error}`,
                life: 3000,
            });
            console.error('Word dosyası oluşturma hatası:', error);
            setLoading(false);
        }
    };

    return (
        <div className="bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-white">
            <style jsx>{`
    /* TabView Styling - Light Mode */
    .p-tabview .p-tabview-nav {
        background: #f8f9fa;
        border-color: #dee2e6;
        border-width: 0 0 2px 0;
        padding: 0;
    }
    .p-tabview .p-tabview-nav li .p-tabview-nav-link {
        background: #f0f0f0;
        color: #495057;
        border: 1px solid #dee2e6;
        border-bottom: none;
        border-radius: 4px 4px 0 0;
        margin-right: 2px;
        font-size: 0.875rem;
        padding: 0.5rem 1rem;
        transition: background-color 0.2s;
    }
    .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
        background: #ffffff;
        color: #3b82f6;
        border-color: #3b82f6 #dee2e6 transparent #dee2e6;
        border-top-width: 3px;
    }
    .p-tabview .p-tabview-nav li:not(.p-highlight):not(.p-disabled):hover .p-tabview-nav-link {
        background: #e9ecef;
        color: #495057;
    }
    
    /* TabView Styling - Dark Mode */
    .dark .p-tabview .p-tabview-nav {
        background: #1f2937;
        border-color: #374151;
        border-width: 0 0 2px 0;
    }
    .dark .p-tabview .p-tabview-nav li .p-tabview-nav-link {
        background: #1e293b;
        color: #94a3b8;
        border: 1px solid #374151;
        border-bottom: none;
        border-radius: 4px 4px 0 0;
    }
    .dark .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
        background: #2d3748;
        color: #ffffff;
        border-color: #4f46e5 #374151 transparent #374151;
        border-top-width: 3px;
    }
    .dark .p-tabview .p-tabview-nav li:not(.p-highlight):not(.p-disabled):hover .p-tabview-nav-link {
        background: #2d3748;
        color: #e5e7eb;
    }

    .dark .p-tabview .p-tabview-panels {
            padding: 0.5rem !important;
            background: #2d3748;
        color: #e5e7eb;
        }
        .p-tabview .p-tabview-panels {
            padding: 0.5rem !important;
        }
    
    /* Mobile padding adjustments */
    @media (max-width: 640px) {
        .p-card .p-card-body {
            padding: 0.5rem !important;
        }
        .p-card .p-card-content {
            padding: 0.5rem 0 !important;
        }
        .dark .p-tabview .p-tabview-panels {
            padding: 0.5rem !important;
            background: #2d3748;
        color: #e5e7eb;
        }
        .p-tabview .p-tabview-panels {
            padding: 0.5rem !important;
        }
        .p-tabview .p-tabview-nav li .p-tabview-nav-link {
            padding: 0.35rem 0.5rem;
            font-size: 0.75rem;
        }
        
        /* Other mobile optimizations */
        .p-dropdown {
            height: 2rem;
        }
        .p-dropdown .p-dropdown-label {
            padding: 0.25rem 0.5rem;
        }
        .p-calendar .p-inputtext {
            padding: 0.25rem 0.5rem;
            height: 2rem;
        }
        .p-button {
            padding: 0.25rem 0.5rem;
        }
        
        /* Card component spacing */
        .p-card h3 {
            margin-bottom: 0.5rem;
        }
        .p-card .grid {
            gap: 0.5rem !important;
        }
    }
    
    /* Keep existing styles */
    .alert-blink {
        animation: blink 1s infinite;
    }
    @keyframes blink {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
    }
    
    .p-dialog-mask {
        backdrop-filter: blur(5px);
        background-color: rgba(0, 0, 0, 0.4) !important;
    }
    
    .p-dialog {
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3) !important;
    }

    @media (max-width: 768px) {
        .p-datatable-wrapper {
            overflow-x: auto;
        }
        .p-datatable .p-datatable-thead > tr > th {
            font-size: 0.75rem;
            padding: 0.5rem 0.25rem;
        }
        .p-datatable .p-datatable-tbody > tr > td {
            font-size: 0.75rem;
            padding: 0.5rem 0.25rem;
        }
    }
`}</style>
            <Toast ref={toast} />

            {/* Header ve Filtre Satırı */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
                {/* Başlık ve Word Butonu */}
                <Card className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white border-gray-200 dark:border-gray-700 h-fit px-2 sm:px-3">
                    <div className="flex flex-row justify-between items-center gap-1">
                        <div className="flex items-center">
                            <h2 className="text-base sm:text-xl md:text-2xl font-bold">Ödeme Takip Paneli</h2>
                        </div>
                        {/* <Button
                            label={window.innerWidth < 640 ? "Word" : "Word'e Aktar"}
                            icon="pi pi-file-word"
                            onClick={exportToWord}
                            className="hidden sm:inline-flex p-button-outlined p-button-primary dark:p-button-outlined dark:p-button-secondary text-xs sm:text-sm p-1 sm:p-2"
                        /> */}
                    </div>
                </Card>

                {/* Filtre Alanı */}
                <Card className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white border-gray-200 dark:border-gray-700 p-x2 sm:px-3">
                    <div className="flex justify-between items-center ">
                        <h3 className="text-sm sm:text-lg md:text-xl font-bold">Filtreler</h3>
                        <div className="flex gap-1 sm:gap-2">
                            <Button
                                label={window.innerWidth < 640 ? "" : "Filtrele"}
                                icon="pi pi-filter"
                                onClick={() => setActiveFilterPanel(!activeFilterPanel)}
                                className="p-button-sm p-button-outlined p-button-primary dark:p-button-outlined dark:p-button-secondary text-xs sm:text-sm p-1 sm:p-2"
                            />
                            <Button
                                label={window.innerWidth < 640 ? "" : "Sıfırla"}
                                icon="pi pi-refresh"
                                onClick={resetFilters}
                                className="p-button-sm p-button-outlined p-button-danger text-xs sm:text-sm p-1 sm:p-2"
                            />
                        </div>
                    </div>

                    {activeFilterPanel && (
                        <div className="flex flex-col sm:flex-row gap-1 sm:gap-3 p-1 sm:p-3 rounded bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white">
                            <div className="flex flex-col flex-1">
                                <label htmlFor="timeRange" className="font-bold mb-0.5 sm:mb-1 text-xs sm:text-sm">Dönem</label>
                                <Dropdown
                                    id="timeRange"
                                    options={timeRangeOptions}
                                    value={selectedTimeRange}
                                    onChange={(e) => setSelectedTimeRange(e.value)}
                                    className="w-full text-xs sm:text-sm"
                                />
                            </div>
                            <div className="flex flex-col flex-1">
                                <label htmlFor="customer" className="font-bold mb-0.5 sm:mb-1 text-xs sm:text-sm">Müşteri</label>
                                <Dropdown
                                    id="customer"
                                    options={customerOptions}
                                    value={selectedCustomer}
                                    onChange={(e) => setSelectedCustomer(e.value)}
                                    className="w-full text-xs sm:text-sm"
                                    placeholder="Müşteri Seçin"
                                    filter
                                    showClear
                                />
                            </div>
                            <div className="flex flex-col flex-1">
                                <label htmlFor="dateRange" className="font-bold mb-0.5 sm:mb-1 text-xs sm:text-sm">Tarih Aralığı</label>
                                <Calendar
                                    id="dateRange"
                                    value={dateRange}
                                    onChange={(e) => setDateRange(e.value)}
                                    selectionMode="range"
                                    readOnlyInput
                                    className="w-full text-xs sm:text-sm"
                                    placeholder="Tarih Aralığı"
                                    showButtonBar
                                    showIcon
                                />
                            </div>
                        </div>
                    )}
                </Card>
            </div>

            {/* Alert for Due Today Payments */}
            {dueTodayVisible && (
                <div className="mb-2 sm:mb-3 p-2 sm:p-3 rounded bg-orange-600 dark:bg-orange-800 border-orange-500 dark:border-orange-700 text-white alert-blink">
                    <div className="flex flex-row justify-between items-center gap-1">
                        <div className="flex items-center flex-1">
                            <i className="pi pi-calendar mr-2 text-sm sm:text-base"></i>
                            <span className="font-bold text-xs sm:text-sm md:text-base">Dikkat! Bugün ödenecek {dueTodayPayments.length} adet ödeme bulunmaktadır.</span>
                        </div>
                        <Button
                            label={window.innerWidth < 640 ? "Detay" : "Detayları Gör"}
                            className="p-button-warning p-button-outlined p-1 sm:p-2 text-white border-white text-xs sm:text-sm flex-shrink-0"
                            onClick={() => {
                                setTablesVisible(true);
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Alert for Overdue Payments */}
            {overdueVisible && (
                <div ref={alertRef} className="mb-2 sm:mb-3 p-2 sm:p-3 rounded transition-opacity duration-500 bg-red-800 dark:bg-red-900 border-red-500 dark:border-red-700 text-red-100 dark:text-red-100">
                    <div className="flex flex-row justify-between items-center gap-1">
                        <div className="flex items-center flex-1">
                            <i className="pi pi-exclamation-triangle mr-2 text-sm sm:text-base"></i>
                            <span className="font-bold text-xs sm:text-sm md:text-base">Dikkat! {overduePayments.length} adet gecikmiş ödeme bulunmaktadır.</span>
                        </div>
                        <Button
                            label={window.innerWidth < 640 ? "Detay" : "Detayları Gör"}
                            className="p-button-danger p-button-outlined p-1 sm:p-2 text-xs sm:text-sm flex-shrink-0"
                            onClick={() => setOverdueDialogVisible(true)}
                        />
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            <Card className="mb-2 sm:mb-3 bg-white dark:bg-gray-800 text-gray-800 dark:text-white border-gray-200 dark:border-gray-700 w-full">
                <div className="flex justify-between items-center mb-2 sm:mb-3">
                    <h3 className="text-base sm:text-lg md:text-xl font-bold">Özet Bilgiler</h3>
                    <Button
                        icon={summaryVisible ? "pi pi-minus" : "pi pi-plus"}
                        className="p-button-rounded p-button-text p-button-primary dark:p-button-text dark:p-button-secondary text-xs sm:text-sm"
                        onClick={() => setSummaryVisible(!summaryVisible)}
                    />
                </div>

                {summaryVisible && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
                        <Card className="text-center col-span-2 sm:col-span-3 lg:col-span-2 bg-gray-50 dark:bg-gray-500 text-gray-800 dark:text-white rounded-lg p-2 sm:p-3">
                            <h3 className="text-sm sm:text-base md:text-lg">Toplam Ödemeler</h3>
                            <div className="text-xl sm:text-2xl md:text-3xl font-bold">{summaryData.totalPayments}</div>
                            <div className="text-sm sm:text-base md:text-lg">{formatCurrency(summaryData.totalAmount)}</div>
                        </Card>
                        <Card className="text-center bg-green-50 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-lg p-2 sm:p-3">
                            <h3 className="text-xs sm:text-sm md:text-base">Ödenen</h3>
                            <div className="text-lg sm:text-xl md:text-2xl font-bold">{summaryData.paidPayments}</div>
                            <div className="text-xs sm:text-sm md:text-base">{formatCurrency(summaryData.paidAmount)}</div>
                        </Card>
                        <Card className="text-center bg-yellow-50 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 rounded-lg p-2 sm:p-3">
                            <h3 className="text-xs sm:text-sm md:text-base">Bekleyen</h3>
                            <div className="text-lg sm:text-xl md:text-2xl font-bold">{summaryData.pendingPayments}</div>
                            <div className="text-xs sm:text-sm md:text-base">{formatCurrency(summaryData.pendingAmount)}</div>
                        </Card>
                        <Card className="text-center bg-orange-50 dark:bg-orange-900 text-orange-600 dark:text-orange-300 rounded-lg p-2 sm:p-3">
                            <h3 className="text-xs sm:text-sm md:text-base">Bugün</h3>
                            <div className="text-lg sm:text-xl md:text-2xl font-bold">{summaryData.dueTodayPayments}</div>
                            <div className="text-xs sm:text-sm md:text-base">{formatCurrency(summaryData.dueTodayAmount)}</div>
                            {summaryData.dueTodayPayments > 0 && (
                                <Button
                                    label="Detay"
                                    className="p-button-sm p-button-outlined p-button-warning mt-1 sm:mt-2 text-xs"
                                    onClick={() => {
                                        setTablesVisible(true);
                                    }}
                                />
                            )}
                        </Card>
                        <Card className="text-center bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-300 rounded-lg p-2 sm:p-3">
                            <h3 className="text-xs sm:text-sm md:text-base">Gecikmiş</h3>
                            <div className="text-lg sm:text-xl md:text-2xl font-bold">{summaryData.overduePayments}</div>
                            <div className="text-xs sm:text-sm md:text-base">{formatCurrency(summaryData.overdueAmount)}</div>
                            {summaryData.overduePayments > 0 && (
                                <Button
                                    label="Detay"
                                    className="p-button-sm p-button-outlined p-button-danger mt-1 sm:mt-2 text-xs"
                                    onClick={() => setOverdueDialogVisible(true)}
                                />
                            )}
                        </Card>
                    </div>
                )}
            </Card>

            {/* Tables and Charts side by side */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
                {/* Data Tables */}
                <Card className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white border-gray-200 dark:border-gray-700">
                    <div className="text-gray-800 dark:text-white flex justify-between items-center mb-2 sm:mb-3">
                        <h3 className="text-base sm:text-lg md:text-xl font-bold">Ödeme Tabloları</h3>
                        <Button
                            icon={tablesVisible ? "pi pi-minus" : "pi pi-plus"}
                            className="p-button-rounded p-button-text p-button-primary dark:p-button-text dark:p-button-secondary text-xs sm:text-sm"
                            onClick={() => setTablesVisible(!tablesVisible)}
                        />
                    </div>

                    {tablesVisible && (
                        <TabView className="w-full">
                            <TabPanel header="Bugün" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">
                                <DataTable
                                    value={getTodayPayments()}
                                    responsiveLayout="scroll"
                                    emptyMessage="Bugün ödenecek ödeme bulunmamaktadır."
                                    paginator
                                    rows={5}
                                    className="custom-datatable text-xs sm:text-sm"
                                    scrollable
                                    scrollHeight="300px"
                                >
                                    <Column
                                        field="installmentOrder"
                                        header="Taksit"
                                        sortable
                                        style={{ minWidth: '60px' }}
                                        body={(rowData) => (
                                            <div className="font-bold text-center text-xs sm:text-sm">
                                                {rowData?.isPartialPayment?.id === 0
                                                    ? `${rowData?.installmentOrder}`
                                                    : `${rowData?.installmentOrder}-${rowData?.partialPaymentInstallmentOrder}`}
                                            </div>
                                        )}
                                    />
                                    <Column
                                        field="customerName"
                                        header="Müşteri"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm truncate max-w-[100px]">
                                                {`${rowData.customerName || ''} ${rowData.customerSurname || ''}`}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                    <Column
                                        field="totalAmountOfInstallments"
                                        header="Toplam"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm">
                                                {formatCurrency(rowData.totalAmountOfInstallments)}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                    <Column
                                        field="isPaid"
                                        header="Durum"
                                        body={(rowData) => (
                                            <div className="flex justify-center items-center">
                                                {paymentStatusTemplate(rowData)}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '100px' }}
                                    />
                                </DataTable>
                            </TabPanel><TabPanel header="Gecikmiş" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">
                                <DataTable
                                    value={overduePayments}
                                    responsiveLayout="scroll"
                                    emptyMessage="Gecikmiş ödeme bulunmamaktadır."
                                    paginator
                                    rows={5}
                                    className="custom-datatable text-xs sm:text-sm"
                                    scrollable
                                    scrollHeight="300px"
                                >
                                    <Column
                                        field="installmentOrder"
                                        header="Taksit"
                                        sortable
                                        style={{ minWidth: '60px' }}
                                        body={(rowData) => (
                                            <div className="font-bold text-center text-xs sm:text-sm">
                                                {rowData?.isPartialPayment?.id === 0
                                                    ? `${rowData?.installmentOrder}`
                                                    : `${rowData?.installmentOrder}-${rowData?.partialPaymentInstallmentOrder}`}
                                            </div>
                                        )}
                                    />
                                    <Column
                                        field="customerName"
                                        header="Müşteri"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm truncate max-w-[100px]">
                                                {`${rowData.customerName || ''} ${rowData.customerSurname || ''}`}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                    <Column
                                        field="installmentDate"
                                        header="Tarih"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm">
                                                {formatDate(rowData.installmentDate)}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                    <Column
                                        field="totalAmountOfInstallments"
                                        header="Toplam"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm">
                                                {formatCurrency(rowData.totalAmountOfInstallments)}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                    <Column
                                        field="actions"
                                        header="İşlem"
                                        body={(rowData) => (
                                            <div className="flex justify-center items-center">
                                                <Button
                                                    icon="pi pi-eye"
                                                    className="p-button-sm text-xs"
                                                    onClick={() => showOverdueDetail(rowData)}
                                                />
                                            </div>
                                        )}
                                        style={{ minWidth: '60px' }}
                                    />
                                </DataTable>
                            </TabPanel>
                            <TabPanel header="Yaklaşan" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">
                                <DataTable
                                    value={getUpcomingPayments()}
                                    responsiveLayout="scroll"
                                    emptyMessage="Yaklaşan ödeme bulunmamaktadır."
                                    paginator
                                    rows={5}
                                    className="custom-datatable text-xs sm:text-sm"
                                    scrollable
                                    scrollHeight="300px"
                                >
                                    <Column
                                        field="installmentOrder"
                                        header="Taksit"
                                        sortable
                                        style={{ minWidth: '60px' }}
                                        body={(rowData) => (
                                            <div className="font-bold text-center text-xs sm:text-sm">
                                                {rowData?.isPartialPayment?.id === 0
                                                    ? `${rowData?.installmentOrder}`
                                                    : `${rowData?.installmentOrder}-${rowData?.partialPaymentInstallmentOrder}`}
                                            </div>
                                        )}
                                    />
                                    <Column
                                        field="customerName"
                                        header="Müşteri"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm truncate max-w-[100px]">
                                                {`${rowData.customerName || ''} ${rowData.customerSurname || ''}`}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                    <Column
                                        field="installmentDate"
                                        header="Tarih"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm">
                                                {formatDate(rowData.installmentDate)}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                    <Column
                                        field="totalAmountOfInstallments"
                                        header="Toplam"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm">
                                                {formatCurrency(rowData.totalAmountOfInstallments)}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                </DataTable>
                            </TabPanel>
                            <TabPanel header="Son Ödemeler" className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white">
                                <DataTable
                                    value={getRecentPayments()}
                                    responsiveLayout="scroll"
                                    emptyMessage="Son ödeme bulunmamaktadır."
                                    paginator
                                    rows={5}
                                    className="custom-datatable text-xs sm:text-sm"
                                    scrollable
                                    scrollHeight="300px"
                                >
                                    <Column
                                        field="installmentOrder"
                                        header="Taksit"
                                        sortable
                                        style={{ minWidth: '60px' }}
                                        body={(rowData) => (
                                            <div className="font-bold text-center text-xs sm:text-sm">
                                                {rowData?.isPartialPayment?.id === 0
                                                    ? `${rowData?.installmentOrder}`
                                                    : `${rowData?.installmentOrder}-${rowData?.partialPaymentInstallmentOrder}`}
                                            </div>
                                        )}
                                    />
                                    <Column
                                        field="customerName"
                                        header="Müşteri"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm truncate max-w-[100px]">
                                                {`${rowData.customerName || ''} ${rowData.customerSurname || ''}`}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                    <Column
                                        field="paidDate"
                                        header="Ödeme Tarihi"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm">
                                                {formatDate(rowData.paidDate)}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '90px' }}
                                    />
                                    <Column
                                        field="totalAmountOfInstallments"
                                        header="Toplam"
                                        body={(rowData) => (
                                            <div className="text-xs sm:text-sm">
                                                {formatCurrency(rowData.totalAmountOfInstallments)}
                                            </div>
                                        )}
                                        sortable
                                        style={{ minWidth: '80px' }}
                                    />
                                </DataTable>
                            </TabPanel>

                        </TabView>
                    )}
                </Card>

                {/* Charts */}
                <Card className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-2 sm:mb-3">
                        <h3 className="text-base sm:text-lg md:text-xl font-bold">Ödeme Analizi</h3>
                        <Button
                            icon={chartsVisible ? "pi pi-minus" : "pi pi-plus"}
                            className="p-button-rounded p-button-text p-button-primary dark:p-button-text dark:p-button-secondary text-xs sm:text-sm"
                            onClick={() => setChartsVisible(!chartsVisible)}
                        />
                    </div>

                    {chartsVisible && (
                        <TabView className="w-full">
                            <TabPanel header="Aylık" className="bg-white dark:bg-gray-800">
                                <div className="mb-2 sm:mb-2 flex items-center gap-2">
                                    <label htmlFor="yearSelector" className=" font-bold whitespace-nowrap">Yıl:</label>
                                    <Dropdown
                                        id="yearSelector"
                                        value={selectedChartYear}
                                        options={availableYears.map(year => ({ label: year.toString(), value: year }))}
                                        onChange={(e) => setSelectedChartYear(e.value)}
                                        className="w-24 min-w-fit bg-white dark:bg-gray-700 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 text-xs "
                                    />
                                </div>

                                {chartData ? (
                                    <div className="h-48 sm:h-64 md:h-80 w-full">
                                        <Chart
                                            type="bar"
                                            data={chartData}
                                            options={{
                                                plugins: {
                                                    legend: {
                                                        position: 'top',
                                                        labels: {
                                                            color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#495057',
                                                            font: {
                                                                size: window.innerWidth < 640 ? 10 : 12
                                                            }
                                                        },
                                                        title: {
                                                            display: true,
                                                            text: `${selectedChartYear} Yılı Aylık Dağılım`,
                                                            color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#495057',
                                                            font: {
                                                                size: window.innerWidth < 640 ? 12 : 14
                                                            }
                                                        }
                                                    },
                                                    tooltip: {
                                                        titleFont: {
                                                            size: window.innerWidth < 640 ? 10 : 12
                                                        },
                                                        bodyFont: {
                                                            size: window.innerWidth < 640 ? 10 : 12
                                                        },
                                                        callbacks: {
                                                            label: function (context) {
                                                                let label = context.dataset.label || '';
                                                                if (label) {
                                                                    label += ': ';
                                                                }
                                                                if (context.parsed.y !== null) {
                                                                    label += new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(context.parsed.y);
                                                                }
                                                                return label;
                                                            }
                                                        }
                                                    }
                                                },
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                scales: {
                                                    x: {
                                                        ticks: {
                                                            color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#495057',
                                                            font: {
                                                                size: window.innerWidth < 640 ? 8 : 10
                                                            }
                                                        },
                                                        grid: {
                                                            color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                                                        }
                                                    },
                                                    y: {
                                                        beginAtZero: true,
                                                        ticks: {
                                                            color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#495057',
                                                            font: {
                                                                size: window.innerWidth < 640 ? 8 : 10
                                                            },
                                                            callback: function (value) {
                                                                return new Intl.NumberFormat('tr-TR', {
                                                                    style: 'currency',
                                                                    currency: 'TRY',
                                                                    minimumFractionDigits: 0,
                                                                    maximumFractionDigits: 0
                                                                }).format(value);
                                                            }
                                                        },
                                                        grid: {
                                                            color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                                                        }
                                                    }
                                                }
                                            }}
                                            className="w-full h-full"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex justify-center items-center h-48 sm:h-64 md:h-80">
                                        <ProgressSpinner style={{ width: '30px', height: '30px' }} className="sm:w-10 sm:h-10" />
                                    </div>
                                )}
                            </TabPanel>
                            <TabPanel header="Özet" className="bg-white dark:bg-gray-800">
                                {chartData && (
                                    <div className="flex flex-col lg:flex-row gap-2 sm:gap-4">
                                        <div className="flex-1 min-w-0 h-48 sm:h-56 md:h-64">
                                            <Chart
                                                type="doughnut"
                                                data={{
                                                    labels: ['Ödendi', 'Bekliyor', 'Bugün', 'Gecikti'],
                                                    datasets: [
                                                        {
                                                            data: [
                                                                summaryData.paidAmount || 0.001,
                                                                summaryData.pendingAmount || 0.001,
                                                                summaryData.dueTodayAmount || 0.001,
                                                                summaryData.overdueAmount || 0.001
                                                            ],
                                                            backgroundColor: [
                                                                chartColors.paid.light,
                                                                chartColors.pending.light,
                                                                chartColors.dueToday.light,
                                                                chartColors.overdue.light
                                                            ],
                                                            hoverBackgroundColor: [
                                                                'rgba(54, 162, 235, 1)',
                                                                'rgba(255, 206, 86, 1)',
                                                                'rgba(255, 165, 0, 1)',
                                                                'rgba(255, 99, 132, 1)'
                                                            ]
                                                        }
                                                    ]
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            position: 'bottom',
                                                            labels: {
                                                                color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#495057',
                                                                boxWidth: window.innerWidth < 640 ? 8 : 10,
                                                                font: {
                                                                    size: window.innerWidth < 640 ? 8 : 10
                                                                }
                                                            }
                                                        },
                                                        title: {
                                                            display: true,
                                                            text: 'Durum Dağılımı',
                                                            color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#495057',
                                                            font: {
                                                                size: window.innerWidth < 640 ? 10 : 12
                                                            }
                                                        },
                                                        tooltip: {
                                                            titleFont: {
                                                                size: window.innerWidth < 640 ? 10 : 12
                                                            },
                                                            bodyFont: {
                                                                size: window.innerWidth < 640 ? 10 : 12
                                                            },
                                                            callbacks: {
                                                                label: function (tooltipItem) {
                                                                    return `${tooltipItem.label}: ${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(tooltipItem.raw)}`;
                                                                }
                                                            }
                                                        }
                                                    }
                                                }}
                                                className="w-full h-full"
                                            />
                                        </div>

                                        <div className="flex-1 min-w-0 h-48 sm:h-56 md:h-64">
                                            <Chart
                                                type="pie"
                                                data={{
                                                    labels: ['Anapara', 'Faiz'],
                                                    datasets: [
                                                        {
                                                            data: [
                                                                summaryData.totalPrincipal || 0.001,
                                                                summaryData.totalInterest || 0.001
                                                            ],
                                                            backgroundColor: [
                                                                chartColors.principal.light,
                                                                chartColors.interest.light
                                                            ],
                                                            hoverBackgroundColor: [
                                                                'rgba(75, 192, 192, 1)',
                                                                'rgba(153, 102, 255, 1)'
                                                            ]
                                                        }
                                                    ]
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: {
                                                            position: 'bottom',
                                                            labels: {
                                                                color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#495057',
                                                                boxWidth: window.innerWidth < 640 ? 8 : 10,
                                                                font: {
                                                                    size: window.innerWidth < 640 ? 8 : 10
                                                                }
                                                            }
                                                        },
                                                        title: {
                                                            display: true,
                                                            text: 'Anapara/Faiz',
                                                            color: document.documentElement.classList.contains('dark') ? '#ffffff' : '#495057',
                                                            font: {
                                                                size: window.innerWidth < 640 ? 10 : 12
                                                            }
                                                        },
                                                        tooltip: {
                                                            titleFont: {
                                                                size: window.innerWidth < 640 ? 10 : 12
                                                            },
                                                            bodyFont: {
                                                                size: window.innerWidth < 640 ? 10 : 12
                                                            },
                                                            callbacks: {
                                                                label: function (tooltipItem) {
                                                                    return `${tooltipItem.label}: ${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(tooltipItem.raw)}`;
                                                                }
                                                            }
                                                        }
                                                    }
                                                }}
                                                className="w-full h-full"
                                            />
                                        </div>
                                    </div>
                                )}
                            </TabPanel>
                        </TabView>
                    )}
                </Card>
            </div>

            {/* Full Data Table */}
            <Card className="mb-2 sm:mb-3 bg-white dark:bg-gray-800 text-gray-800 dark:text-white border-gray-200 dark:border-gray-700 w-full">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 sm:mb-3 gap-2">
                    <h3 className="text-base sm:text-lg md:text-xl font-bold">Tüm Ödemeler</h3>
                    <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                        <span className="text-xs sm:text-sm">
                            {filteredData.length} ödeme
                            {selectedCustomer && ` (${selectedCustomer})`}
                        </span>
                        {dateRange[0] && dateRange[1] && (
                            <span className="text-xs sm:text-sm">
                                ({formatDate(dateRange[0])} - {formatDate(dateRange[1])})
                            </span>
                        )}
                    </div>
                </div>
                <DataTable
                    value={filteredData}
                    responsiveLayout="scroll"
                    paginator
                    rows={window.innerWidth < 640 ? 5 : 10}
                    emptyMessage="Ödeme kaydı bulunmamaktadır."
                    className="custom-datatable text-xs sm:text-sm"
                    scrollable
                    scrollHeight="400px"
                >
                    <Column
                        field="installmentOrder"
                        header="Taksit"
                        sortable
                        style={{ minWidth: '60px' }}
                        body={(rowData) => (
                            <div className="font-bold text-center text-xs sm:text-sm">
                                {rowData?.isPartialPayment?.id === 0
                                    ? `${rowData?.installmentOrder}`
                                    : `${rowData?.installmentOrder}-${rowData?.partialPaymentInstallmentOrder}`}
                            </div>
                        )}
                    />
                    <Column
                        field="customerName"
                        header="Müşteri"
                        body={(rowData) => (
                            <div className="text-xs sm:text-sm truncate max-w-[100px]">
                                {`${rowData.customerName || ''} ${rowData.customerSurname || ''}`}
                            </div>
                        )}
                        sortable
                        style={{ minWidth: '80px' }}
                    />
                    <Column
                        field="installmentDate"
                        header="Taksit Tarihi"
                        body={(rowData) => (
                            <div className="text-xs sm:text-sm">
                                {formatDate(rowData.installmentDate)}
                            </div>
                        )}
                        sortable
                        style={{ minWidth: '90px' }}
                        className=" sm:table-cell"
                    />
                    <Column
                        field="installmentPrincipalAmount"
                        header="Anapara"
                        body={(rowData) => (
                            <div className="text-xs sm:text-sm">
                                {formatCurrency(rowData.installmentPrincipalAmount)}
                            </div>
                        )}
                        sortable
                        style={{ minWidth: '80px' }}
                        className=" lg:table-cell"
                    />
                    <Column
                        field="installmentInterestAmount"
                        header="Faiz"
                        body={(rowData) => (
                            <div className="text-xs sm:text-sm">
                                {formatCurrency(rowData.installmentInterestAmount)}
                            </div>
                        )}
                        sortable
                        style={{ minWidth: '80px' }}
                        className=" lg:table-cell"
                    />
                    <Column
                        field="totalAmountOfInstallments"
                        header="Toplam"
                        body={(rowData) => (
                            <div className="text-xs sm:text-sm font-semibold">
                                {formatCurrency(rowData.totalAmountOfInstallments)}
                            </div>
                        )}
                        sortable
                        style={{ minWidth: '80px' }}
                    />
                    <Column
                        field="isPaid"
                        header="Durum"
                        body={(rowData) => (
                            <div className="flex justify-center items-center">
                                {paymentStatusTemplate(rowData)}
                            </div>
                        )}
                        sortable
                        style={{ minWidth: '100px' }}
                    />
                    <Column
                        field="paidDate"
                        header="Ödeme Tarihi"
                        body={(rowData) => (
                            <div className="text-xs sm:text-sm">
                                {rowData.paidDate ? formatDate(rowData.paidDate) : '-'}
                            </div>
                        )}
                        sortable
                        style={{ minWidth: '90px' }}
                        className=" xl:table-cell"
                    />
                </DataTable>
            </Card>

            {/* Overdue Payment Dialog */}
            <Dialog
                header="Gecikmiş Ödeme Detayları"
                visible={overdueDialogVisible}
                onHide={() => setOverdueDialogVisible(false)}
                style={{ width: window.innerWidth < 768 ? '95vw' : window.innerWidth < 1024 ? '80vw' : '60vw' }}
                className="bg-white dark:bg-gray-800"
                contentClassName="bg-white dark:bg-gray-800 text-gray-800 dark:text-white p-2 sm:p-4"
                headerClassName="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white text-sm sm:text-base"
            >
                {!selectedOverduePayment && overduePayments.length > 0 ? (
                    <div className="p-2 sm:p-4">
                        <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-4">Gecikmiş Ödemeler</h3>
                        <DataTable
                            value={overduePayments}
                            responsiveLayout="scroll"
                            paginator
                            rows={5}
                            className="custom-datatable text-xs sm:text-sm"
                            scrollable
                            scrollHeight="300px"
                        >
                            <Column
                                field="installmentOrder"
                                header="Taksit"
                                sortable
                                style={{ minWidth: '60px' }}
                                body={(rowData) => (
                                    <div className="text-xs sm:text-sm">
                                        {rowData?.installmentOrder}
                                    </div>
                                )}
                            />
                            <Column
                                field="customerName"
                                header="Müşteri"
                                body={(rowData) => (
                                    <div className="text-xs sm:text-sm">
                                        {`${rowData.customerName || ''} ${rowData.customerSurname || ''}`}
                                    </div>
                                )}
                                sortable
                                style={{ minWidth: '120px' }}
                                className=" sm:table-cell"
                            />
                            <Column
                                field="installmentDate"
                                header="Tarih"
                                body={(rowData) => (
                                    <div className="text-xs sm:text-sm">
                                        {formatDate(rowData.installmentDate)}
                                    </div>
                                )}
                                sortable
                                style={{ minWidth: '80px' }}
                            />
                            <Column
                                field="totalAmountOfInstallments"
                                header="Toplam"
                                body={(rowData) => (
                                    <div className="text-xs sm:text-sm">
                                        {formatCurrency(rowData.totalAmountOfInstallments)}
                                    </div>
                                )}
                                sortable
                                style={{ minWidth: '80px' }}
                            />
                            <Column
                                field="actions"
                                header="İşlem"
                                body={(rowData) => (
                                    <div className="flex justify-center items-center">
                                        <Button
                                            icon="pi pi-eye"
                                            label={window.innerWidth < 640 ? "" : "Detay"}
                                            className="p-button-sm p-button-danger text-xs"
                                            onClick={() => setSelectedOverduePayment(rowData)}
                                        />
                                    </div>
                                )}
                                style={{ minWidth: '80px' }}
                            />
                        </DataTable>
                    </div>
                ) : selectedOverduePayment ? (
                    <div className="p-2 sm:p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                            <div className="col-span-1 sm:col-span-2 p-2 sm:p-3 rounded text-center bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-300">
                                <div className="text-base sm:text-lg font-bold">Gecikmiş Ödeme</div>
                                <div className="text-xs sm:text-sm">
                                    Son Ödeme: {formatDate(selectedOverduePayment.installmentDate)}
                                </div>
                                <div className="text-xs sm:text-sm">
                                    Gecikme: {Math.floor((new Date() - new Date(selectedOverduePayment.installmentDate)) / (1000 * 60 * 60 * 24))} gün
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                                <p className="font-bold text-xs sm:text-sm">Müşteri:</p>
                                <p className="text-xs sm:text-sm">{`${selectedOverduePayment.customerName || ''} ${selectedOverduePayment.customerSurname || ''}`}</p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                                <p className="font-bold text-xs sm:text-sm">Taksit No:</p>
                                <p className="text-xs sm:text-sm">
                                    {selectedOverduePayment?.isPartialPayment?.id === 0
                                        ? selectedOverduePayment?.installmentOrder
                                        : `${selectedOverduePayment?.installmentOrder} - ${selectedOverduePayment?.partialPaymentInstallmentOrder}`}
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                                <p className="font-bold text-xs sm:text-sm">Anapara:</p>
                                <p className="text-xs sm:text-sm">{formatCurrency(selectedOverduePayment.installmentPrincipalAmount)}</p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                                <p className="font-bold text-xs sm:text-sm">Faiz:</p>
                                <p className="text-xs sm:text-sm">{formatCurrency(selectedOverduePayment.installmentInterestAmount)}</p>
                            </div>

                            <div className="col-span-1 sm:col-span-2 flex flex-col sm:flex-row gap-1 sm:gap-2">
                                <p className="font-bold text-xs sm:text-sm">Toplam:</p>
                                <p className="text-sm sm:text-lg md:text-xl text-red-600 dark:text-red-400 font-bold">
                                    {formatCurrency(selectedOverduePayment.totalAmountOfInstallments)}
                                </p>
                            </div>
                        </div>

                        <Divider className="border-gray-200 dark:border-gray-700 my-2 sm:my-4" />

                        <div className="flex flex-col sm:flex-row justify-between gap-2">
                            <Button
                                label="Geri"
                                icon="pi pi-arrow-left"
                                onClick={() => setSelectedOverduePayment(null)}
                                className="p-1 sm:p-2 bg-green-500 hover:bg-green-600 text-white text-xs sm:text-sm"
                            />
                            <Button
                                label="Kapat"
                                icon="pi pi-times"
                                onClick={() => {
                                    setSelectedOverduePayment(null);
                                    setOverdueDialogVisible(false);
                                }}
                                className="p-1 sm:p-2 bg-red-500 hover:bg-red-600 text-white text-xs sm:text-sm"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex justify-center items-center p-3 sm:p-5">
                        <p className="text-xs sm:text-sm">Gecikmiş ödeme bulunmamaktadır.</p>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default Dashboard;